import { useEffect, useState } from 'react'
import { Breadcrumb, Col, Offcanvas, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import NoDataForViewIlustrationReferee from '../../../assets/svg/NoDataForViewIlustrationReferee'
import Button from '../../../components/Button'
import Dropdown from '../../../components/Dropdown'
import RadioButton from '../../../components/RadioButton'
import SearchInput from '../../../components/SearchInput'
import TableRefereePagination from '../../../components/TableRefereePagination'
import { useAppContext } from '../../../lib/contextLib'

import Add from '../../../assets/svg/Add'
import OutlinedFilter from '../../../assets/svg/OutlinedFilter'
import TableRefereeCard from '../../../components/TableRefereeCard'

import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { getCodebook, getRefereeLicense, getReferees } from '../../../lib/api'
import { debounce, formatDate } from '../../../lib/helpers'
import { font } from '../Competitions/CompetitionProfile/font'

import { RowsPerPageOptions } from '../../../components/RowsPerPageDropdown'
import { exportCSV, exportExcel } from './csvExport'
import './styles.scss'

const searchInputID = `searchInput-referees`

const Referee = () => {
    const { setIsLoading, user } = useAppContext()
    //pagination
    const [referees, setReferees] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [pageCount, setPageCount] = useState(1)
    const [total, setTotal] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(RowsPerPageOptions[0])

    const initialFilters = {
        gender: '',
        region: '',
        core_license: '',
        national_license_type: '',
        international_main_badge: '',
        international_chair_badge: '',
    }

    const [filterParams, setFilterParams] = useState(initialFilters)

    //* Codebooks
    const [genders, setGenders] = useState([])
    const [regions, setRegions] = useState([])
    const [internationalMainRefereeBadges, setInternationalMainRefereeBadges] =
        useState([])
    const [
        internationalChairRefereeBadges,
        setInternationalChairRefereeBadges,
    ] = useState([])
    const [refereeCoreLicenses, setRefereeCoreLicenses] = useState([])
    const [refereeNationalLicenses, setRefereeNationalLicenses] = useState([])

    const handleCodebooks = async () => {
        await getCodebook('codebooks.gender', setGenders)
        await getCodebook('codebooks.region', setRegions)
        await getCodebook(
            'codebooks.referee-international-main-badge',
            setInternationalMainRefereeBadges
        )
        await getCodebook(
            'codebooks.referee-international-chair-badge',
            setInternationalChairRefereeBadges
        )
        await getCodebook(
            'codebooks.referee-core-license',
            setRefereeCoreLicenses
        )
        await getCodebook(
            'codebooks.referee-national-license-type',
            setRefereeNationalLicenses
        )
    }

    const [showFilters, setShowFilters] = useState(false)

    const extractSearchParameters = (searchValue) => {
        const [given_name, family_name] = searchValue.split(' ')
        return { given_name, family_name }
    }

    const fetchAllRefereesAndLicenses = async (
        fields,
        searchParams,
        filters,
        page,
        allEntries = false,
        rowsPerPage
    ) => {
        const { allReferees, pgCount, total } = await getReferees(
            fields,
            searchParams,
            filters,
            page,
            allEntries,
            rowsPerPage
        )

        if (allReferees) {
            const refereesWithLicenses = await Promise.all(
                allReferees.map(async (referee) => {
                    referee.attributes.referees_licenses =
                        await getRefereeLicense(referee?.attributes?.uuid)
                    return referee
                })
            )

            return { refereesWithLicenses, pgCount, total }
        }

        return null
    }

    const getAllReferees = async (
        page,
        presetFilters = filterParams,
        allEntries = false
    ) => {
        const searchElement = document.getElementById(searchInputID)

        if (!searchElement) return

        setIsLoading(true)

        const searchParams = extractSearchParameters(searchElement.value)
        const fields = []
        const filtersToUse = presetFilters || filterParams

        const result = await fetchAllRefereesAndLicenses(
            fields,
            searchParams,
            filtersToUse,
            page,
            allEntries,
            rowsPerPage
        )

        if (result) {
            const { refereesWithLicenses, pgCount, total } = result

            if (allEntries) {
                setIsLoading(false)
                return refereesWithLicenses
            } else {
                setReferees(refereesWithLicenses)
                setPageCount(pgCount)
                setTotal(total)
                setCurrentPage(page)
                setShowFilters(false)
            }
        }

        setIsLoading(false)
    }

    useEffect(() => {
        getAllReferees(currentPage)
        handleCodebooks()
    }, [currentPage, rowsPerPage])

    const updateDebounceText = debounce(async function (searchValue) {
        setCurrentPage(1)
        setPageCount(1)
        getAllReferees(1)
    }, 1000)

    // Remove filters
    const handleDisableFiltering = () => {
        setFilterParams(initialFilters)
        setShowFilters(false)
        getAllReferees(1, initialFilters)
        setCurrentPage(1)
        setPageCount(1)
    }

    const handleCsvExport = async () => {
        await exportCSV(filterParams, searchInputID, setIsLoading)
        setShowFilters(false)
    }

    const handleExcelExport = async () => {
        await exportExcel(filterParams, searchInputID, setIsLoading)
        setShowFilters(false)
    }

    const handlePdfExport = async () => {
        const filteredReferees = await getAllReferees(1, filterParams, true)
        setIsLoading(true)
        const unit = 'pt'
        const size = 'A4' // Use A1, A2, A3 or A4
        const orientation = 'landscape' // portrait or landscape

        const marginLeft = 40
        const doc = new jsPDF(orientation, unit, size)
        doc.addFileToVFS('WorkSans-normal.ttf', font)
        doc.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal')
        doc.setFont('WorkSans')
        doc.setFontSize(15)

        const countHelper = () => {
            let rowsPerPage = 10
            return `${(currentPage - 1) * rowsPerPage + 1}-${
                rowsPerPage * currentPage
            }`
        }

        const title = `Sudije - Ukupno: ${
            filteredReferees?.length
        }, ${formatDate(new Date()).slice(0, -1)}.`
        doc.text(title, marginLeft, 40)

        const rows = []

        for (let i = 1; i <= filteredReferees?.length; i++) {
            const r = filteredReferees[i - 1]
            rows.push([
                r?.attributes?.person_info?.given_name +
                    ' ' +
                    r?.attributes?.person_info?.family_name,
                r?.attributes.region?.region_name
                    ? r?.attributes.region?.region_name
                    : 'Nema informacije',
                r?.attributes?.referees_licenses?.attributes?.core_license
                    ? r?.attributes?.referees_licenses?.attributes?.core_license
                          ?.core_license_name
                    : 'Nema informacije',
                r?.attributes?.referees_licenses?.attributes
                    ?.national_license_type?.national_license_type_name
                    ? r?.attributes?.referees_licenses?.attributes
                          ?.national_license_type?.national_license_type_name
                    : 'Nema informacije',
                r?.attributes?.referees_licenses?.attributes
                    ?.international_chair_badge?.international_chair_badge_name
                    ? r?.attributes?.referees_licenses?.attributes
                          ?.international_chair_badge
                          ?.international_chair_badge_name
                    : 'Nema informacije',
                r?.attributes?.referees_licenses?.attributes
                    ?.international_main_badge?.international_main_badge_name
                    ? r?.attributes?.referees_licenses?.attributes
                          ?.international_main_badge
                          ?.international_main_badge_name
                    : 'Nema informacije',
            ])
        }

        let refereeContent = {
            startY: 50,
            head: [
                [
                    'Sudija',
                    'Region',
                    'Licenca',
                    'Tip licence',
                    'Bedž (sudija u stolici)',
                    'Bedž (glavni sudija)',
                ],
            ],
            body: rows,
            styles: {
                font: 'WorkSans',
            },
        }

        doc.autoTable(refereeContent)
        doc.save(
            `Sudije_Ukupno:${filteredReferees?.length}_${formatDate(
                new Date()
            ).slice(0, -1)}.pdf`
        )

        setShowFilters(false)
        setIsLoading(false)
    }

    return (
        <Row className="referee-wrapper h-100">
            {referees ? (
                <Col>
                    <Row className="pt-3">
                        <Col
                            xs={12}
                            md={4}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <Row>
                                <Col>
                                    <h2 className="d-none d-md-block">
                                        Sudije
                                    </h2>
                                    <p className="d-block d-md-none">Sudije</p>
                                    <Breadcrumb className="mt-md-2 mt-1">
                                        <Breadcrumb.Item
                                            linkAs={Link}
                                            linkProps={{ to: '/pocetna' }}
                                        >
                                            Početna
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item active>
                                            Sudije
                                        </Breadcrumb.Item>
                                    </Breadcrumb>
                                </Col>
                            </Row>

                            <div className="d-flex align-items-center d-md-none">
                                <Button
                                    type="text"
                                    onClick={() => setShowFilters(true)}
                                >
                                    <OutlinedFilter />
                                </Button>

                                {user?.role?.type === 'admin' && (
                                    <Link to="/sudije/kreiraj-novog-sudiju">
                                        <Button type="text">
                                            <Add />
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        </Col>
                        <Col xs={12} md={8}>
                            <Row className="d-flex align-items-center">
                                <div className="d-flex justify-content-end w-100 mt-2 mt-md-0">
                                    <SearchInput
                                        id={searchInputID}
                                        placeholder="Pronađi sudiju po imenu i prezimenu"
                                        onChange={(e) =>
                                            updateDebounceText(e.target.value)
                                        }
                                    />
                                    <Button
                                        type="secondary"
                                        className="mx-2 d-none d-md-block"
                                        onClick={() => setShowFilters(true)}
                                    >
                                        Filtriraj
                                    </Button>

                                    {user?.role?.type === 'admin' && (
                                        <Link
                                            className="d-none d-md-block"
                                            to="/sudije/kreiraj-novog-sudiju"
                                            tabIndex="1"
                                        >
                                            <Button>Kreiraj sudiju</Button>
                                        </Link>
                                    )}
                                </div>
                            </Row>
                        </Col>
                    </Row>

                    <Row className="m-0 pt-3">
                        <TableRefereePagination
                            referees={referees}
                            className="d-none d-md-block"
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageCount={pageCount}
                            total={total}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                        />

                        <TableRefereeCard
                            className={'d-md-none d-flex background-table-card'}
                            referees={referees}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageCount={pageCount}
                            total={total}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                        />
                    </Row>
                </Col>
            ) : (
                <Col
                    md={12}
                    className="p-0 d-flex flex-column align-items-center justify-content-center"
                >
                    <NoDataForViewIlustrationReferee />
                    <h2 className="mt-5">Sudije</h2>
                    <p className="mt-2 text-center">
                        Još uvek nema kreiranih sudija. Klikni na dugme "Kreiraj
                        sudiju" da bi ste kreirali novog.
                    </p>
                    <Link
                        to="/sudije/kreiraj-novog-sudiju"
                        style={{ textDecoration: 'none' }}
                        tabIndex="1"
                    >
                        <Button className="mt-4">Kreiraj sudiju</Button>
                    </Link>
                </Col>
            )}

            <Offcanvas
                show={showFilters}
                onHide={() => setShowFilters(false)}
                placement="end"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h3>Filtriraj</h3>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <hr className="p-0 mt-2 mb-2" />
                <Offcanvas.Body>
                    <Row>
                        <Col xs={12}>
                            <RadioButton
                                name="Pol"
                                radioValues={genders}
                                checkedValue={filterParams?.gender || ''}
                                onClick={(e) => {
                                    filterParams?.gender === e.target.value
                                        ? setFilterParams({
                                              ...filterParams,
                                              gender: '',
                                          })
                                        : null
                                }}
                                onChange={(e) =>
                                    setFilterParams({
                                        ...filterParams,
                                        gender: e.target.value,
                                    })
                                }
                            />

                            <Dropdown
                                label={'Region'}
                                value={filterParams?.region || ''}
                                placeholder="Region"
                                handleSelect={(e) => {
                                    setFilterParams({
                                        ...filterParams,
                                        region: e === 'Odselektuj' ? '' : e,
                                    })
                                }}
                                dropdownLinks={regions?.reduce(
                                    (accumulator, region) => [
                                        ...accumulator,
                                        region,
                                    ],
                                    filterParams?.region ? ['Odselektuj'] : []
                                )}
                                className="mt-3"
                            />
                            <Dropdown
                                label={'Licenca'}
                                value={filterParams?.core_license || ''}
                                placeholder="Licenca"
                                handleSelect={(e) => {
                                    setFilterParams({
                                        ...filterParams,
                                        core_license:
                                            e === 'Odselektuj' ? '' : e,
                                    })
                                }}
                                dropdownLinks={refereeCoreLicenses?.reduce(
                                    (accumulator, core_license) => [
                                        ...accumulator,
                                        core_license,
                                    ],
                                    filterParams?.core_license
                                        ? ['Odselektuj']
                                        : []
                                )}
                                className="mt-3"
                            />
                            <Dropdown
                                label={'Tip licence nacionalnog sudije'}
                                value={
                                    filterParams?.national_license_type || ''
                                }
                                placeholder="Tip licence"
                                handleSelect={(e) => {
                                    setFilterParams({
                                        ...filterParams,
                                        national_license_type:
                                            e === 'Odselektuj' ? '' : e,
                                    })
                                }}
                                dropdownLinks={refereeNationalLicenses?.reduce(
                                    (accumulator, national_license_type) => [
                                        ...accumulator,
                                        national_license_type,
                                    ],
                                    filterParams?.national_license_type
                                        ? ['Odselektuj']
                                        : []
                                )}
                                className="mt-3"
                            />

                            <Dropdown
                                label={'Bedž glavnog sudije'}
                                value={
                                    filterParams?.international_main_badge || ''
                                }
                                placeholder="Tip bedža glavnog sudije"
                                handleSelect={(e) => {
                                    setFilterParams({
                                        ...filterParams,
                                        international_main_badge:
                                            e === 'Odselektuj' ? '' : e,
                                    })
                                }}
                                dropdownLinks={internationalMainRefereeBadges?.reduce(
                                    (accumulator, international_main_badge) => [
                                        ...accumulator,
                                        international_main_badge,
                                    ],
                                    filterParams?.international_main_badge
                                        ? ['Odselektuj']
                                        : []
                                )}
                                className="mt-3"
                            />
                            <Dropdown
                                label={'Bedž sudije u stolici'}
                                value={
                                    filterParams?.international_chair_badge ||
                                    ''
                                }
                                placeholder="Tip bedža sudije u stolici"
                                handleSelect={(e) => {
                                    setFilterParams({
                                        ...filterParams,
                                        international_chair_badge:
                                            e === 'Odselektuj' ? '' : e,
                                    })
                                }}
                                dropdownLinks={internationalChairRefereeBadges?.reduce(
                                    (
                                        accumulator,
                                        international_chair_badge
                                    ) => [
                                        ...accumulator,
                                        international_chair_badge,
                                    ],
                                    filterParams?.international_chair_badge
                                        ? ['Odselektuj']
                                        : []
                                )}
                                className="mt-3"
                            />
                        </Col>

                        <Col md={12} className="mt-3">
                            <Button
                                className="w-100"
                                onClick={() => getAllReferees(1)}
                            >
                                Prikaži rezultate
                            </Button>
                        </Col>
                        <Col md={12} className="mt-3">
                            <Button
                                type="secondary"
                                className="w-100"
                                onClick={handlePdfExport}
                            >
                                Štampaj rezultate
                            </Button>
                        </Col>
                        <Col md={12} className="mt-3">
                            <Button
                                type="secondary"
                                className="w-100"
                                onClick={handleExcelExport}
                            >
                                Eksport Excel
                            </Button>
                        </Col>
                        <Col md={12} className="mt-3">
                            <Button
                                type="secondary"
                                className="w-100"
                                onClick={handleCsvExport}
                            >
                                Eksport CSV
                            </Button>
                        </Col>
                        <Col md={12} className="mt-3">
                            <Button
                                type="text"
                                className="w-100"
                                onClick={() => handleDisableFiltering()}
                            >
                                Ukloni filtriranje
                            </Button>
                        </Col>
                    </Row>
                </Offcanvas.Body>
            </Offcanvas>
        </Row>
    )
}

export default Referee
